/**
 * TextInputController encapsulates the hidden textarea + event-capturing mask.
 * It listens to keyboard/mouse/touch/IME events and manipulate the text via
 * api in TextProvider.
 */

import { TextNode } from '../../render/text';
import { MouseButton, on } from '../../render/utils/dom';
import { buildBindingMap, handleShortcutEvent, shortCuts, type BindingMap } from './shortcuts';
import type { TextProvider } from './text';

export type StageContextLike = any; // keep loose to avoid hard deps; expects container, api, interactive, providers

export type Point = { x: number; y: number };

export class TextInputController {
  private stageContext: StageContextLike | null = null;
  private container: HTMLElement | null = null;

  /**
   * textarea is used to receive IME/input and keyboard events.
   * It is kept invisible.
   */
  private textarea: HTMLTextAreaElement | null = null;
  /**
   * maskEl is a transparent div overlaying the text node region to capture
   * mouse/touch events for selection.
   */
  private maskEl: HTMLElement | null = null;

  // state
  private activeNodeId: string | null = null;
  private _isActive = false;
  private _isDraggingMask = false;
  private _isComposing = false;

  private _teardownCallbacks: (() => void)[] = [];
  private _bindingMap: BindingMap | null = null;

  // NOTE: we avoid storing handler refs; on() returns teardown functions kept in _teardownCallbacks.
  constructor(private provider: TextProvider) {
    this.stageContext = provider.renderContext;
    this.container = this.stageContext?.container ?? null;
  }

  get isComposing() {
    return this._isComposing;
  }

  get active() {
    return this._isActive;
  }

  // no subscription API; controller acts directly on provider

  activate(node: { id: string; fontSize?: number; letterSpacing?: number; font?: any } | string) {
    const nodeId = typeof node === 'string' ? node : node?.id;
    if (!nodeId) return;
    this.activeNodeId = nodeId;
    this._isActive = true;

    this._ensureMask();
    this._activateTextarea();

    // build shortcut binding map lazily on first activation
    if (!this._bindingMap) {
      this._bindingMap = buildBindingMap(shortCuts);
    }

    // style textarea per node font so IME/caret height matches
    const n = typeof node === 'string' ? this.getNode(node) : node;
    if (this.textarea && n) {
      // allow provider to update more precise font via external call if needed
      this.textarea.style.letterSpacing = `${n.letterSpacing || 0}px`;
      this.textarea.value = '';
      // keep offscreen but focused
      this.textarea.focus();
    }

    this._showMask();
    this.updateInputElmPosition();

    // wait a tick for dom to update
    // then focus textarea
    setTimeout(() => {
      this.textarea?.focus();
    });
  }

  focus() {
    return this.textarea?.focus();
  }

  deactivate() {
    this._isActive = false;
    this.activeNodeId = null;
    this._isDraggingMask = false;
    this._hideMask();
    this.textarea?.blur();
  }

  destroy() {
    this.deactivate();
    // remove event listeners and DOM nodes
    // First run all teardown callbacks (listeners)
    for (const off of this._teardownCallbacks.splice(0)) {
      try {
        off();
      } catch {}
    }
    this._teardownMask();
    this._teardownTextarea();
    this.stageContext = null;
    this.container = null;
  }

  updateInputElmPosition() {
    if (!this._isActive || !this.activeNodeId) return;
    const mask = this.maskEl;
    const ta = this.textarea;
    if (!mask || !ta || !this.stageContext) return;
    const node = this.getNode(this.activeNodeId);
    if (!node) return;

    // compute transform/size based on node's current box/region in stage
    const {
      api,
      dpr = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
      providers,
    } = this.stageContext as any;
    const box = api?.getNodeBox?.(node);
    const region = api?.getNodeRegion?.(node)?.rotate?.(node.rotation ?? 0);
    if (!box || !region) return;

    const scale = providers?.scale;
    const zoom = scale ? scale.scale / dpr : 1 / dpr;
    const leftPx = (region.center.x / dpr) * (scale?.scale ?? 1) + (scale?.translateX ?? 0) / dpr;
    const topPx = (region.center.y / dpr) * (scale?.scale ?? 1) + (scale?.translateY ?? 0) / dpr;

    const transformStr = `translate(${leftPx}px, ${topPx}px) translate(-50%, -50%) scale(${zoom}) rotate(${
      (region.angle ?? 0) / 2
    }deg)`;

    const apply = (el: HTMLElement) => {
      el.style.transform = transformStr;
      el.style.transformOrigin = 'center';
      el.style.width = `${box.w}px`;
      el.style.height = `${box.h}px`;
      el.style.left = '0px';
      el.style.top = '0px';
    };
    apply(mask);
  }

  // DOM helpers
  private _activateTextarea() {
    if (this.textarea || typeof document === 'undefined') return;

    const ta = document.createElement('textarea');
    ta.setAttribute('autocomplete', 'off');
    ta.setAttribute('autocorrect', 'off');
    ta.setAttribute('autocapitalize', 'off');
    ta.setAttribute('spellcheck', 'false');
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    ta.style.fontSize = `${this.provider.currentNode?.fontSize || 16}px`;
    ta.style.width = '100%';
    ta.style.height = '100%';

    this._isComposing = false;
    // IME
    const onCompStart = () => {
      this._isComposing = true;
      ta.style.fontSize = `${this.provider.currentNode?.fontSize || 16}px`;
      ta.value = this.provider.currentNode?.text || '';
      const [start, end] = this.provider.getCurrentSelection();
      ta.setSelectionRange(start, end);
    };
    const onCompUpdate = (e: CompositionEvent) => {
      this.provider.replaceSelectedText(e.data || '', {
        collapsed: false,
        addHistory: false,
      });
    };
    const onCompEnd = (e: CompositionEvent) => {
      this._isComposing = false;
      const text = e.data || '';
      this.provider.replaceSelectedText(text);
      ta.value = '';
    };
    this._teardownCallbacks.push(on(ta, 'compositionstart', onCompStart));
    this._teardownCallbacks.push(on(ta, 'compositionupdate', onCompUpdate));
    this._teardownCallbacks.push(on(ta, 'compositionend', onCompEnd));

    const onInput = (e: InputEvent) => {
      if (this._isComposing || e.isComposing === true) return;
      const inputType = e.inputType || '';

      switch (inputType) {
        case 'insertText':
          if (e.data) this.provider.insertText(e.data);
          return;
        case 'insertLineBreak':
        case 'insertParagraph':
          this.provider.insertText('\n');
          return;
        case 'deleteContentBackward':
          this.provider.deleteText(-1);
          return;
        case 'deleteContentForward':
          this.provider.deleteText(1);
          return;
        case 'deleteWordBackward':
          this.provider.deleteWord(-1);
          return;
        case 'deleteWordForward':
          this.provider.deleteWord(1);
          return;
        case 'insertFromPaste':
          if (e.data) this.provider.paste();
          return;
        default:
          break;
        // the following inputType may not fire if textarea is empty
        // so we need to handle them in keydown
        // 1. deleteByCut
        // 2. historyUndo
        // 3. historyRedo
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!this._isActive || this._isComposing) return;
      const { key } = e;

      let handled = false;
      if (key === 'Escape') {
        this.provider.cancel();
        handled = true;
      } else if (this._bindingMap && handleShortcutEvent(e, this._bindingMap, this.provider)) {
        handled = true;
      }
      if (handled) {
        e.preventDefault();
      }
    };
    const onCopy = (e: ClipboardEvent) => {
      if (!this._isActive) return;

      e.preventDefault();
      const normalizedSel = this.provider.normalizedSelection;

      e.clipboardData?.setData('text/plain', this.provider.getTextInRange(normalizedSel));
    };

    this._teardownCallbacks.push(
      // some event may not fire if textarea is empty, so
      // we use `beforeinput` to try to catch all input event
      on(ta, 'beforeinput', onInput),
      on(ta, 'keydown', onKeyDown),
      on(ta, 'copy', onCopy)
    );

    // attach DOM
    this.maskEl?.appendChild(ta);
    this.textarea = ta;
  }

  private _teardownTextarea() {
    const ta = this.textarea;
    if (!ta) return;
    ta.remove();
    this.textarea = null;
  }

  private _ensureMask() {
    if (this.maskEl || typeof document === 'undefined' || !this.container) return;
    const mask = document.createElement('div');
    mask.className = 'text-edit-mask';
    mask.style.position = 'absolute';
    mask.style.left = '0';
    mask.style.top = '0';
    mask.style.width = '0';
    mask.style.height = '0';
    mask.style.cursor = 'text';
    mask.style.background = this.stageContext?.debug ? '#e31a1a87' : 'transparent';
    mask.style.pointerEvents = 'auto';
    mask.style.zIndex = '2';
    mask.style.display = 'none';

    const canvas = this.container.querySelector('canvas');
    if (canvas && canvas.nextSibling) this.container.insertBefore(mask, canvas.nextSibling);
    else if (canvas) canvas.after(mask);
    else this.container.appendChild(mask);

    const onMouseDown = (e: MouseEvent) => {
      if (!this._isActive) return;
      if (e.button === MouseButton.RIGHT) return;
      e.stopPropagation();
      e.preventDefault();
      const point = this.stageContext?.interactive?.eventToPoint?.(e);
      if (point) this.provider.beginSelectionFromPoint(point);
      this._isDraggingMask = true;
      this.textarea?.focus();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!this._isActive || !this._isDraggingMask) return;
      e.stopPropagation();
      e.preventDefault();
      const point = this.stageContext?.interactive?.eventToPoint?.(e);
      if (point) this.provider.updateSelectionToPoint(point);
    };
    const onMouseUp = (e: MouseEvent) => {
      if (!this._isActive || !this._isDraggingMask) return;
      e.stopPropagation();
      e.preventDefault();
      this._isDraggingMask = false;
      this.provider.endSelection();
      this.textarea?.focus();
    };
    const onDblClick = (e: MouseEvent) => {
      if (!this._isActive) return;
      e.stopPropagation();
      e.preventDefault();
      const point = this.stageContext?.interactive?.eventToPoint?.(e);
      if (point) this.provider.doubleClickAtPoint(point);
      this.textarea?.focus();
    };
    const onTouchStart = (e: TouchEvent) => {
      if (!this._isActive) return;
      if (!e.touches || e.touches.length === 0) return;
      const t = e.touches[0];
      const fake = {
        clientX: t.clientX,
        clientY: t.clientY,
        target: mask,
      } as any;
      const point = this.stageContext?.interactive?.eventToPoint?.(fake);
      if (point) this.provider.beginSelectionFromPoint(point);
      this._isDraggingMask = true;
      this.textarea?.focus();
      e.stopPropagation();
      e.preventDefault();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!this._isActive || !this._isDraggingMask) return;
      if (!e.touches || e.touches.length === 0) return;
      const t = e.touches[0];
      const fake = {
        clientX: t.clientX,
        clientY: t.clientY,
        target: mask,
      } as any;
      const point = this.stageContext?.interactive?.eventToPoint?.(fake);
      if (point) this.provider.updateSelectionToPoint(point);
      e.stopPropagation();
      e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!this._isActive || !this._isDraggingMask) return;
      this._isDraggingMask = false;
      this.provider.endSelection();
      e.stopPropagation();
      e.preventDefault();
    };

    this._teardownCallbacks.push(
      on(mask, 'mousedown', onMouseDown),
      on(window, 'mousemove', onMouseMove, true),
      on(window, 'mouseup', onMouseUp, true),
      on(mask, 'dblclick', onDblClick),
      on(mask, 'touchstart', onTouchStart as any, { passive: false }),
      on(window, 'touchmove', onTouchMove as any, {
        passive: false,
        capture: true,
      }),
      on(window, 'touchend', onTouchEnd as any, {
        passive: false,
        capture: true,
      })
    );

    this.maskEl = mask;
  }

  private _teardownMask() {
    const mask = this.maskEl;
    if (!mask) return;
    mask.remove();
    this.maskEl = null;
  }

  private _showMask() {
    if (this.maskEl) this.maskEl.style.display = 'block';
  }
  private _hideMask() {
    if (this.maskEl) this.maskEl.style.display = 'none';
  }

  private getNode(id: string): TextNode | null {
    return this.stageContext?.api?.getNode?.(id) ?? null;
  }
}
